import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({ message: z.string(), code: z.string().optional() }),
});

/**
 * Leave block schema - represents approved leave periods that block scheduling
 */
export const leaveBlockSchema = z.object({
  id: z.number(),
  userId: z.number(),
  start: z.string(), // ISO datetime
  end: z.string(), // ISO datetime
  leaveTypeId: z.number().nullable(),
  leaveTypeName: z.string().nullable(),
  isApproved: z.boolean(),
  remark: z.string().nullable(),
});

/**
 * Capacity slot schema - represents booked hours per user per day
 */
export const capacitySlotSchema = z.object({
  userId: z.number(),
  date: z.string(), // ISO date (YYYY-MM-DD)
  bookedHours: z.number(), // Sum of scheduled hours for this user on this day
  availableHours: z.number(), // Working hours available (default 8)
  utilizationPercent: z.number(), // (bookedHours / availableHours) * 100
});

/**
 * Resource availability response - aggregates capacity and leave for a date range
 */
export const resourceAvailabilitySchema = z.object({
  userId: z.number(),
  userName: z.string(),
  dateRange: z.object({
    start: z.string(), // ISO date
    end: z.string(), // ISO date
  }),
  capacitySlots: z.array(capacitySlotSchema),
  leaveBlocks: z.array(leaveBlockSchema),
  totalBookedHours: z.number(),
  totalAvailableHours: z.number(),
  averageUtilization: z.number(), // Percent
});

export const availabilityContract = c.router({
  /**
   * Get leave blocks for resources in date range
   */
  getAvailability: {
    method: 'GET',
    path: '/availability',
    query: z.object({
      start: z.string().datetime({ message: 'start must be a valid ISO 8601 datetime' }),
      end: z.string().datetime({ message: 'end must be a valid ISO 8601 datetime' }),
      userIds: z.string().optional(), // Comma-separated user IDs (e.g., "1,2,3")
    }),
    responses: {
      200: z.array(leaveBlockSchema),
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get approved leave blocks in date range',
  },

  /**
   * Get capacity utilization for resources in date range
   */
  getCapacity: {
    method: 'GET',
    path: '/capacity',
    query: z.object({
      start: z.string().datetime({ message: 'start must be a valid ISO 8601 datetime' }),
      end: z.string().datetime({ message: 'end must be a valid ISO 8601 datetime' }),
      userIds: z.string().optional(), // Comma-separated user IDs
    }),
    responses: {
      200: z.array(capacitySlotSchema),
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get capacity utilization per user per day',
  },

  /**
   * Get combined availability (capacity + leave) for resources
   */
  getResourceAvailability: {
    method: 'GET',
    path: '/resource-availability',
    query: z.object({
      start: z.string().datetime({ message: 'start must be a valid ISO 8601 datetime' }),
      end: z.string().datetime({ message: 'end must be a valid ISO 8601 datetime' }),
      userIds: z.string().optional(),
    }),
    responses: {
      200: z.array(resourceAvailabilitySchema),
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get combined capacity and leave data per resource',
  },
});

export type LeaveBlock = z.infer<typeof leaveBlockSchema>;
export type CapacitySlot = z.infer<typeof capacitySlotSchema>;
export type ResourceAvailability = z.infer<typeof resourceAvailabilitySchema>;
